import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Avatar,
  Typography,
  Button,
  Divider,
  Switch,
} from "@mui/material";
import { useAuth, useAddTalent, useCurrentTalentProfile } from "../../../hooks";
import { toggleCoffeeCallAvailabilityFn } from "../../../services/api/talent.api";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import {
  BusinessAccount,
  BusinessProfileInfo,
  Talent,
  User,
} from "../../../types/user";
import useCurrentGTP from "../../../hooks/useGTP";
import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetUserProfile } from "../../../services/api/user.api";
import GuestLiteModal from "../../../pages/Auth/GuestLiteModal";
import usebusinessInfo from "../../../hooks/useBusiness";
import useCurrentBusinessInfo, {
  useCurrentBusinessStore,
} from "../../../hooks/useCurrentBusiness";
import { useGetBusinessProfile } from "../../../services/api/business/business.api";
import useThemeStore from "../../../services/store/useThemeStore";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const TalentandUserToggleMenu = ({
  talentListClose,
}: {
  talentListClose?: () => void;
}) => {
  const { userInfo, setUser } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { setAddTalent } = useAddTalent();
  const { SetTalentProfile, currentProfile } = useCurrentTalentProfile();
  const { currentGTP, SetCurrentGTP } = useCurrentGTP();
  const navigate = useNavigate();
  const [isCoffeeCallActive, setIsCoffeeCallActive] = React.useState(
    !!currentProfile?.coffe_call_enabled,
  );
  const { t } = useTranslation();
  const [talentAvailability, setTalentAvailability] = React.useState<
    Record<string, boolean>
  >({});
  const queryClient = useQueryClient();

  const isGuestLite = userInfo?.profile_completed === false;
  const [isGuestLiteModalOpen, setIsGuestLiteModalOpen] = useState(false);

  const { businessInfo, setBusinessInfo } = usebusinessInfo();
  const { currentBusiness, setCurrentBusiness } = useCurrentBusinessInfo();

  const allTalentsRef = React.useRef<Talent[]>(userInfo?.talent || []);

  const allBusinessTalentsRef = React.useRef<BusinessProfileInfo[]>(
    businessInfo?.business_accounts ?? [],
  );

  const isCoffeeCallActiveRef = React.useRef<boolean>(isCoffeeCallActive);

  const { data: user, refetch } = useGetUserProfile({
    key: ["user-profile"],
    options: { enabled: false },
  });

  const { refetch: businessRefetch } = useGetBusinessProfile({
    key: ["get-business-profile"],
    options: { enabled: false },
  });

  // const savedTheme = localStorage.getItem("theme");
  // const initialDarkMode = savedTheme === "dark";

  // const [darkMode, setDarkMode] = useState<boolean>(initialDarkMode);

  // React.useEffect(() => {
  //   if (darkMode) {
  //     document.body.classList.add("dark-mode");
  //     localStorage.setItem("theme", "dark");
  //   } else {
  //     document.body.classList.remove("dark-mode");
  //     localStorage.setItem("theme", "light");
  //   }
  // }, [darkMode]);

  const { darkMode, toggleDarkMode } = useThemeStore();
  const airbankAccent = darkMode ? "#fbbf24" : "#ea580c";
  const airbankAccentSoft = darkMode
    ? "rgba(245, 158, 11, 0.14)"
    : "rgba(234, 88, 12, 0.08)";

  const closeMenu = () => {
    // if (typeof talentListClose?.talentListClose === "function") {
    talentListClose && talentListClose();
    // }
  };

  React.useEffect(() => {
    allTalentsRef.current = userInfo?.talent || [];
  }, [userInfo]);

  // Removed this useEffect - it was only for debugging and causing unnecessary re-renders
  // If you need to debug, use React DevTools or add conditional logging

  React.useEffect(() => {
    allBusinessTalentsRef.current = businessInfo?.business_accounts || [];
  }, [businessInfo]);

  React.useEffect(() => {
    setIsCoffeeCallActive(!!currentProfile?.coffe_call_enabled);
    isCoffeeCallActiveRef.current = !!currentProfile?.coffe_call_enabled;
  }, [currentProfile]);

  // Initialize availability state for all talents
  React.useEffect(() => {
    const initialAvailability: Record<string, boolean> = {};
    allTalentsRef.current.forEach((talent) => {
      // Initialize with true as default
      initialAvailability[talent.id] = true;
    });
    setTalentAvailability((prev) => ({ ...prev, ...initialAvailability }));
  }, [userInfo]);

  const handleToggleCoffeeCall = async (checked: boolean) => {
    if (currentProfile) {
      try {
        await toggleCoffeeCallAvailabilityFn(currentProfile.id, {
          coffe_call_enabled: checked,
        });
        SetTalentProfile({ ...currentProfile, coffe_call_enabled: checked });
        isCoffeeCallActiveRef.current = checked;
        setIsCoffeeCallActive(checked);
        enqueueSnackbar(t("coffee_call_status_updated"), {
          variant: "success",
        });
        refetch();
        queryClient.invalidateQueries({
          queryKey: ["talent", currentProfile?.id],
        });
      } catch (err) {
        enqueueSnackbar(t("failed_to_update_status"), { variant: "error" });
      }
    }
  };

  const handleToggleAvailability = (talentId: string, checked: boolean) => {
    setTalentAvailability((prev) => ({
      ...prev,
      [talentId]: checked,
    }));
    enqueueSnackbar(
      checked
        ? "You are available now, you might receive short call bookings"
        : "You are not available now, you will not receive short call bookings",
      { variant: "success" },
    );
  };

  const handleSignOut = () => {
    if (isLoggingOut) return; // Prevent duplicate clicks
    setIsLoggingOut(true);
    localStorage.setItem("isGuestLite", "false");

    enqueueSnackbar(t("signing_you_out"), { variant: "info" });

    setTimeout(() => {
      try {
        // Save the language preference and theme before clearing
        const savedLang = localStorage.getItem("lang");
        const savedTheme = localStorage.getItem("theme");

        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();

        // Restore the language preference
        if (savedLang) {
          localStorage.setItem("lang", savedLang);
        }

        // Restore the theme
        if (savedTheme) {
          localStorage.setItem("theme", savedTheme);
        }

        enqueueSnackbar(t("signed_out_successfully"), {
          variant: "success",
        });
        window.location.href = "/home";
      } catch (error) {
        enqueueSnackbar(t("failed_to_sign_out_try_again"), {
          variant: "error",
        });
        setIsLoggingOut(false); // Allow retry if something fails
      }
    }, 600); // Small delay for UX smoothness
  };

  const renderTalentAccounts = () => {
    const allTalents = allTalentsRef.current;
    if (!allTalents.length) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center" py={3}>
          <SentimentDissatisfiedIcon sx={{ fontSize: 42, color: "#9CA3AF" }} />
          <Typography fontWeight={600} color="#4B5563">
            {t("no_talent_profiles_found")}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => {
                    closeMenu();
                    businessInfo
                      ? navigate("/register-business-talent")
                      : navigate("/register-talent");
                  }
            }
            sx={{
              border: `2px solid ${airbankAccent}`,
              color: airbankAccent,
              fontWeight: 600,
              textTransform: "none",
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              "&:hover": {
                borderColor: darkMode ? "#f59e0b" : "#c2410c",
                backgroundColor: airbankAccentSoft,
              },
            }}
          >
            {t("register_as_a")}{" "}
            {businessInfo ? t("business_talent") : t("talent")}
          </Button>
        </Stack>
      );
    }

    return (
      <>
        {allTalents.map((talent) => {
          const selected = currentProfile?.id === talent.id;
          return (
            <Box
              key={talent.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: selected
                  ? darkMode
                    ? "var(--primary-900)"
                    : "var(--primary-100)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: darkMode
                    ? "var(--primary-1000)"
                    : "var(--primary-200)",
                },
              }}
            >
              <Box
                key={talent.id}
                onClick={() => {
                  SetTalentProfile(talent);
                  SetCurrentGTP(null);
                  navigate("/airbank-studio");
                  refetch();
                  if (user) {
                    setUser(user?.data as User);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  cursor: "pointer",
                }}
              >
                <Avatar
                  src={talent.profile_image ?? ""}
                  alt={talent.username}
                  sx={{ width: 42, height: 42 }}
                >
                  {talent.username?.[0]}
                </Avatar>
                <Box>
                  <Typography
                    fontWeight={600}
                    fontSize={14}
                    sx={{
                      color: darkMode
                        ? "var(--neutralWhite)"
                        : "var(--neutral-800)",
                    }}
                  >
                    {talent.username}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        backgroundColor: darkMode ? "#f59e0b" : "#ea580c",
                        color: "var(--neutralWhite)",
                        fontSize: 11,
                        px: 1,
                        borderRadius: "999px",
                        fontWeight: 500,
                      }}
                    >
                      {t("talent")}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: darkMode
                          ? "var(--neutral-400)"
                          : "var(--neutral-600)",
                      }}
                    >
                      @{talent.username}
                    </Typography>
                  </Box>
                  {/* {selected && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Switch
                        size="small"
                        checked={talentAvailability[talent.id] ?? true}
                        onChange={(e) =>
                          handleToggleAvailability(talent.id, e.target.checked)
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#10B981",
                            "& + .MuiSwitch-track": {
                              backgroundColor: "#10B981",
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            (talentAvailability[talent.id] ?? true)
                              ? darkMode
                                ? "#10B981"
                                : "#059669"
                              : darkMode
                                ? "var(--neutral-500)"
                                : "var(--neutral-500)",
                          fontWeight: 500,
                        }}
                      >
                        {(talentAvailability[talent.id] ?? true)
                          ? "Available for Short Call"
                          : "Not Available for Short Call"}
                      </Typography>
                    </Box>
                  )} */}
                </Box>
              </Box>
              {selected && currentProfile && (
                <Box
                  sx={{
                    border: darkMode
                      ? `2px dashed ${
                          isCoffeeCallActiveRef.current
                            ? "var(--secondary-600)"
                            : "var(--neutral-300)"
                        }`
                      : `2px dashed ${
                          isCoffeeCallActiveRef.current
                            ? "var(--secondary-600)"
                            : "var(--neutral-300)"
                        }`,
                    borderRadius: 2,
                    px: 2.5,
                    py: 1.5,
                    my: 1,
                    gap: 1,
                    ml: "auto",
                    mr: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: isCoffeeCallActiveRef.current
                      ? "var(--secondary-100)"
                      : "var(--neutral-200)",
                    color: darkMode
                      ? "var(--neutralWhite)"
                      : "var(--neutral-800)",
                  }}
                >
                  <Typography
                    fontWeight={600}
                    fontSize={14}
                    sx={{
                      color: "var(--neutral-800)",
                    }}
                  >
                    {t("available_for_coffee_call")}
                  </Typography>
                  <Switch
                    checked={isCoffeeCallActiveRef.current}
                    onChange={(e) => handleToggleCoffeeCall(e.target.checked)}
                    sx={{
                      width: 40,
                      height: 20,
                      padding: 0,
                      "& .MuiSwitch-switchBase": {
                        padding: 0,
                        left: 2,
                        top: "50%",
                        transform: "translateY(-50%)",
                        "&.Mui-checked": {
                          transform:
                            "translateX(calc(40px - 16px - 4px)) translateY(-50%)",
                          color: "var(--neutralWhite)",
                          "& + .MuiSwitch-track": {
                            background: `linear-gradient(114.44deg, var(--secondary-600) 0%, var(--primary-900) 100%)`,
                            opacity: 1,
                            border: 0,
                          },
                        },
                      },
                      "& .MuiSwitch-thumb": {
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.3)",
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                      },
                      "& .MuiSwitch-track": {
                        borderRadius: 20 / 2,
                        backgroundColor: darkMode
                          ? "var(--neutral-600)"
                          : "var(--neutral-300)",
                        opacity: 1,
                        transition: (theme) =>
                          theme.transitions.create(["background-color"]),
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
        <Box display="flex" gap={2} mt={2}>
          <GuestLiteModal
            open={isGuestLiteModalOpen}
            onClose={() => setIsGuestLiteModalOpen(false)}
          />
        </Box>
      </>
    );
  };

  const renderBusinessAccounts = () => {
    const allBusinessAccounts = allBusinessTalentsRef.current;
    if (!allBusinessAccounts.length) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center" py={3}>
          <SentimentDissatisfiedIcon
            sx={{
              fontSize: 42,
              color: darkMode ? "var(--neutral-600)" : "#9CA3AF",
            }}
          />
          <Typography
            fontWeight={600}
            sx={{ color: darkMode ? "var(--neutral-300)" : "#4B5563" }}
          >
            {t("no_business_accounts_found")}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => navigate("/register-business-talent")
            }
            sx={{
              border: `2px solid ${airbankAccent}`,
              color: airbankAccent,
              fontWeight: 600,
              textTransform: "none",
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              backgroundColor: darkMode ? airbankAccentSoft : undefined,
              "&:hover": {
                borderColor: darkMode ? "#f59e0b" : "#c2410c",
                backgroundColor: darkMode
                  ? "rgba(245, 158, 11, 0.2)"
                  : airbankAccentSoft,
              },
            }}
          >
            {t("register_business")}
          </Button>
        </Stack>
      );
    }

    return (
      <>
        {allBusinessAccounts.map((account) => {
          const selected = currentBusiness?.id === account.id;
          return (
            <Box
              key={account.id}
              onClick={() => {
                setCurrentBusiness(account);
                SetCurrentGTP(null);
                businessRefetch().then((result) => {
                  if (result?.data?.data) {
                    setBusinessInfo(result.data.data);
                  }
                  navigate(`/airbank-studio`);
                });
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: selected
                  ? darkMode
                    ? "var(--primary-900)"
                    : "#EEF2FF"
                  : "transparent",
                "&:hover": {
                  backgroundColor: darkMode ? "var(--primary-1000)" : "#F3F4F6",
                },
              }}
            >
              <Avatar
                src={account.profile_image ?? ""}
                alt={account.business_name}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: darkMode ? "var(--primary-700)" : undefined,
                }}
              >
                {account.business_name?.[0]}
              </Avatar>
              <Box>
                <Typography
                  fontWeight={600}
                  fontSize={14}
                  sx={{ color: darkMode ? "var(--neutralWhite)" : undefined }}
                >
                  {account.business_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: darkMode ? "#f59e0b" : "#ea580c",
                      color: "var(--neutralWhite)",
                      fontSize: 11,
                      px: 1,
                      borderRadius: "999px",
                      fontWeight: 500,
                    }}
                  >
                    {account.business_type}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: darkMode ? "var(--neutral-400)" : "#6B7280" }}
                  >
                    @{account.username}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
        <Box display="flex" gap={2} mt={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => {
                    closeMenu();
                    navigate("/register-business-talent");
                  }
            }
            sx={{
              border: `2px solid ${airbankAccent}`,
              color: airbankAccent,
              fontWeight: 600,
              fontSize: 13,
              textTransform: "none",
              borderRadius: 2,
              py: 1.1,
              backgroundColor: darkMode ? airbankAccentSoft : undefined,
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(245, 158, 11, 0.2)"
                  : airbankAccentSoft,
                borderColor: darkMode ? "#f59e0b" : "#c2410c",
              },
            }}
          >
            {t("add_business")}
          </Button>
          <GuestLiteModal
            open={isGuestLiteModalOpen}
            onClose={() => setIsGuestLiteModalOpen(false)}
          />
        </Box>
      </>
    );
  };

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        minWidth: 340,
        fontFamily: "Lexend",
        bgcolor: darkMode ? "rgba(15, 23, 42, 0.94)" : "var(--neutralWhite)",
        color: darkMode ? "var(--neutralWhite)" : "var(--neutral-800)",
        boxShadow: darkMode
          ? "0 16px 42px rgba(2, 6, 23, 0.5)"
          : "0 8px 24px rgba(15, 23, 42, 0.1)",
        border: darkMode
          ? "1px solid rgba(148, 163, 184, 0.16)"
          : "1px solid rgba(15, 23, 42, 0.08)",
        /* Scrollbar styling */
        "&::-webkit-scrollbar": {
          width: 8,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: darkMode ? "var(--neutral-800)" : "#f0f0f0",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: darkMode ? "var(--neutral-600)" : "#c0c0c0",
          borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: darkMode ? "var(--neutral-500)" : "#a0a0a0",
        },
      }}
    >
      <Stack spacing={2}>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          onClick={() => {
            setCurrentBusiness(null);
            SetTalentProfile(null);
            SetCurrentGTP(null);
            userInfo?.account_type === "Business" &&
              businessRefetch().then((result) => {
                if (result?.data?.data) {
                  setBusinessInfo(result.data.data);
                }
              });
            navigate("/airbank-studio");
          }}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: darkMode
              ? "var(--neutral-800)"
              : "var(--neutral-50)",
            "&:hover": {
              backgroundColor: darkMode
                ? "var(--neutral-700)"
                : "var(--neutral-200)",
            },
          }}
        >
          <Avatar
            src={userInfo?.profile_image ?? ""}
            sx={{ bgcolor: "#FDA4AF", width: 48, height: 48 }}
          >
            U
          </Avatar>
          <Box>
            <Typography
              fontWeight={700}
              sx={{
                color: darkMode ? "var(--neutralWhite)" : "var(--neutral-800)",
              }}
            >
              {businessInfo ? businessInfo?.business_email : userInfo?.email}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                component="span"
                sx={{
                  backgroundColor: darkMode ? "#f59e0b" : "#ea580c",
                  color: "var(--neutralWhite)",
                  fontSize: 11,
                  px: 1.5,
                  py: 0.3,
                  borderRadius: "999px",
                  fontWeight: 500,
                }}
              >
                {t("main_account")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)",
                }}
              >
                @{businessInfo ? businessInfo.username : userInfo?.username}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Light/Dark Mode Toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            bgcolor: darkMode ? "var(--neutral-800)" : "var(--neutral-50)",
            color: darkMode ? "var(--neutralWhite)" : "var(--neutral-800)",
            borderRadius: 2,
          }}
        >
          <Typography
            fontWeight={600}
            fontSize={14}
            mr={2}
            sx={{
              color: darkMode ? "var(--neutralWhite)" : "var(--neutral-800)",
            }}
          >
            {t("light_mode")}
          </Typography>
          <Switch
            checked={darkMode}
            onChange={toggleDarkMode}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: darkMode ? "#f59e0b" : "#ea580c",
                "& + .MuiSwitch-track": {
                  backgroundColor: darkMode
                    ? "rgba(245, 158, 11, 0.35)"
                    : "rgba(234, 88, 12, 0.35)",
                },
              },
            }}
          />
          <Typography
            fontWeight={600}
            fontSize={14}
            ml={2}
            sx={{
              color: darkMode ? "var(--neutralWhite)" : "var(--neutral-800)",
            }}
          >
            {t("dark_mode")}
          </Typography>
        </Box>

        <Divider
          sx={{
            bgcolor: darkMode ? "var(--neutral-700)" : "var(--neutral-200)",
          }}
        />

        {userInfo?.account_type !== "Business" && renderTalentAccounts()}
        {userInfo?.account_type === "Business" && renderBusinessAccounts()}

        <Divider
          sx={{
            bgcolor: darkMode ? "var(--neutral-700)" : "var(--neutral-200)",
          }}
        />

        {/* GTP listing intentionally removed from this menu */}
        <Button
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          disabled={isLoggingOut}
          sx={{
            mt: 1.5,
            fontFamily: "Lexend",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            justifyContent: "flex-start",
            color: airbankAccent,
            px: 2,
            py: 1.2,
            backgroundColor: darkMode ? airbankAccentSoft : airbankAccentSoft,
            border: `1.5px solid ${airbankAccent}`,
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(245, 158, 11, 0.2)"
                : "rgba(234, 88, 12, 0.14)",
              borderColor: darkMode ? "#f59e0b" : "#c2410c",
            },
          }}
        >
          {isLoggingOut ? (
            <>
              <CircularProgress
                size={16}
                sx={{
                  color: airbankAccent,
                  mr: 1,
                }}
              />
              {t("signing_out")}
            </>
          ) : (
            t("logout")
          )}
        </Button>
      </Stack>
    </Paper>
  );
};

export default TalentandUserToggleMenu;
